import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';

import Layout from '../../containers/Layout';
import UserManagementPage from './UserManagementPage';
import OrganizationProfilePage from './OrganizationProfilePage';
import BillingPage from './BillingPage';
import profileIcon from '../../assets/images/nav/profile.svg';
import profileActiveIcon from '../../assets/images/nav/profile-active.svg';
import usersIcon from '../../assets/images/my-meetings/users.svg';
import UserService from '../../services/UserService';
import { ORG_ROLES } from '../../constants/main';
import classes from './styles.module.scss';

const SUBMENU_ITEMS = [
  {
    id: 'company-profile',
    label: 'Company Profile',
    path: '/organization-settings/company-profile',
    icon: profileIcon,
    activeIcon: profileActiveIcon,
  },
  {
    id: 'user-management',
    label: 'User Management',
    path: '/organization-settings/user-management',
    icon: usersIcon,
    activeIcon: usersIcon, // Using same icon for active state - can be replaced with active variant if available
  },
  {
    id: 'billing',
    label: 'Billing & Usage',
    path: '/organization-settings/billing',
    icon: profileIcon,
    activeIcon: profileActiveIcon,
  },
];

export default function OrganizationSettingsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;

  // Get current user to check permissions
  const { data: currentUser } = useQuery('me', UserService.getMe);
  const isOrgAdmin = currentUser?.orgRole?.access === ORG_ROLES.OWNER;

  // Filter submenu items based on permissions
  const visibleSubmenuItems = SUBMENU_ITEMS.filter((item) => {
    // User Management only visible to org admins
    if (item.id === 'user-management') {
      return isOrgAdmin;
    }
    // Billing only visible to org admins
    if (item.id === 'billing') {
      return isOrgAdmin;
    }
    // Company Profile visible to all (but will show read-only for non-admins)
    return true;
  });

  // Determine active submenu item
  const activeSubmenuId = visibleSubmenuItems.find((item) =>
    currentPath.startsWith(item.path)
  )?.id || visibleSubmenuItems[0]?.id;

  // Default to company profile if no specific submenu is active
  const defaultSubmenu = visibleSubmenuItems[0];
  const isOnDefaultPath = currentPath === '/organization-settings';

  // Redirect to default submenu if on base path
  React.useEffect(() => {
    if (isOnDefaultPath && defaultSubmenu) {
      navigate(defaultSubmenu.path, { replace: true });
    }
  }, [isOnDefaultPath, navigate, defaultSubmenu]);

  const handleSubmenuClick = (item) => {
    navigate(item.path);
  };

  const renderContent = () => {
    switch (activeSubmenuId) {
      case 'company-profile':
        return <OrganizationProfilePage />;
      case 'user-management':
        return <UserManagementPage />;
      case 'billing':
        return <BillingPage />;
      default:
        return <OrganizationProfilePage />;
    }
  };

  return (
    <Layout hasUserBlock>
      <div className={classes.OrganizationSettingsPage}>
        <div className={classes.container}>
          <div className={classes.sidebar}>
            <h1 className={classes.pageTitle}>Settings</h1>
            <nav className={classes.submenu}>
              {visibleSubmenuItems.map((item, index) => (
                <React.Fragment key={item.id}>
                  {index > 0 && <div className={classes.separator} />}
                  <button
                    type="button"
                    className={`${classes.submenuItem} ${
                      activeSubmenuId === item.id ? classes.active : ''
                    }`}
                    onClick={() => handleSubmenuClick(item)}
                  >
                    <img
                      src={
                        activeSubmenuId === item.id ? item.activeIcon : item.icon
                      }
                      alt={item.label}
                      className={`${classes.menuIcon} ${
                        item.id === 'user-management' ? classes.userManagementIcon : ''
                      }`}
                    />
                    {item.label}
                  </button>
                </React.Fragment>
              ))}
            </nav>
          </div>
          <div className={classes.content}>{renderContent()}</div>
        </div>
      </div>
    </Layout>
  );
}

